import { Providers } from "@microsoft/mgt-react";
import localforage from 'localforage';
import { ConsentFeatures } from "../../config/ConsentConfig";
import { Msal2Provider } from "../providers/Msal2Provider";
import { platformService } from '../services/platform.service';
import { LICENSE_FEATURE, PRODUCT_LICENSE } from "../utils/constants";
import { AxiosResponse } from "axios";

interface FeatureCacheItem {
  enabled: boolean,
  timeCached?: number
}
interface SubscriptionCacheItem {
  status: string,
  timeCached: number
}

const cacheSchema: LocalForageOptions = {
  driver: localforage.INDEXEDDB,
  name: 'tikit',
  storeName: 'features',
  version: 1
};

const invalidationPeriod = 60 * 60 * 1000; // 1 Hr
const storageReadTimeout = 5 * 1000;    // ceiling on a single IndexedDB read before we treat it as a miss
const subscriptionTimeout = 15 * 1000;  // ceiling on the refresh-subscription wait before we answer false
const cache: LocalForage = localforage.createInstance(cacheSchema);
const platformAPI = new platformService();



// We need an dictionary for those calls that check before we return all the features, this way we can 'subscribe' to the cache getting set
const observer: { [id: string]: Array<(enabled) => void> } = {};
const pendingFeature: { [id: string]: Promise<AxiosResponse> } = {};

const pushSubscription = (key: string, callback: (enabled) => void) => {
  if (!observer[key]) observer[key] = [];
  observer[key].push(callback);
}

/**
 * refreshFeatureCache is a one-shot: App.loadPage awaits it once and it is never called again.
 * Every getCachedFeature() subscription is released by that single run — so once it has settled,
 * a later lookup for a key that is still unknown must NOT subscribe, because nothing will ever
 * wake it. This flag lets that path fall through to a direct API check instead.
 */
let refreshSettled = false;

// Resolve every outstanding subscription. Used when the refresh finishes (or fails) so that no
// getCachedFeature() promise — and no component awaiting one, such as SideNav — is left pending.
const flushObservers = (enabled: boolean) => {
  Object.keys(observer).forEach(key => {
    const waiters = observer[key];
    observer[key] = [];
    waiters?.forEach(resolve => resolve(enabled));
  });
};

/**
 * In-memory mirror of the feature store.
 *
 * Feature flags are read constantly on the list path — fetchTicketData alone resolves eleven of
 * them before it can build a query, and more are checked per column — but they only change when
 * refreshFeatureCache runs. Without this, every one of those checks was a separate IndexedDB read.
 *
 * Those reads used to queue behind the sequential writes below, and the pile-up was measured at
 * three seconds per read on a warm desktop. On iOS Safari, where a transaction can stall until the
 * next user interaction, that is what left the list unrendered until something was tapped.
 */
const memory = new Map<string, FeatureCacheItem>();

const isExpired = (item: FeatureCacheItem) =>
  !!item.timeCached && (Date.now() - item.timeCached) > invalidationPeriod;

export const clearFeatureCache = async () => {
  memory.clear();
  await cache.clear();
};

// Features whose cached value carries a timestamp and expires after invalidationPeriod.
// GroupChat and TimeTracking are here because their flag is licence AND a tenant setting -
// the setting half can be toggled mid-session from the settings page, so a permanently
// cached value would stay stale until the next login.
const expiredFeatures = [LICENSE_FEATURE.GroupChat, LICENSE_FEATURE.TimeTracking, PRODUCT_LICENSE.AnalystAccess];
export const refreshFeatureCache = async (): Promise<boolean> => {
  try {
    const { data: { value: features } } = await platformAPI.getAvailableFeatures();

    const entries: [string, FeatureCacheItem][] = Object.values({ ...LICENSE_FEATURE, ...PRODUCT_LICENSE })
      .filter(x => x != LICENSE_FEATURE.ValidSubscription)
      .map(key => {
        const item: FeatureCacheItem = { enabled: features.includes(key) };
        if (expiredFeatures.includes(key)) item.timeCached = Date.now();
        return [key, item];
      });

    // Memory first, so readers racing this never reach storage at all.
    entries.forEach(([key, item]) => memory.set(key, item));

    // Release waiters straight away. Previously this happened only after each key's write had
    // landed, so a caller blocked on the last feature waited out every preceding write.
    entries.forEach(([key, item]) => {
      observer[key]?.forEach(i => i(item.enabled));
      observer[key] = [];
    });

    // The answer for every known key is in memory now, so any getCachedFeature() call from this
    // point on can be satisfied without subscribing - including one whose own readFromStorage race
    // resolves in the next few lines, while the persist below is still in flight. Setting this only
    // in `finally`, after that persist, left exactly that window: the flush above had already run
    // and drained every observer array, so a subscription registered into it during the persist
    // would never be woken by anything and had to ride out the full subscriptionTimeout.
    refreshSettled = true;

    // Persist in parallel rather than one awaited transaction at a time. Failures are tolerable:
    // memory already holds the values for this session.
    await Promise.all(
      entries.map(([key, item]) => cache.setItem<FeatureCacheItem>(key, item).catch(() => undefined))
    );

    return true;
  } catch (err) {
    console.error(err);

    // The fetch failed, so the values it would have published are never coming on this load.
    // Wake every waiter with false rather than leaving getCachedFeature() promises — and the
    // components awaiting them, such as SideNav — pending for the life of the session.
    flushObservers(false);
  } finally {
    // Whether it succeeded or failed, the one-shot has now run. A later lookup for a key that is
    // still unknown must take the direct path in awaitRefresh instead of subscribing.
    refreshSettled = true;
  }

  return false;
}

const hasFeature = (featureName: string): Promise<AxiosResponse<any>> => {
  if (!pendingFeature[featureName]) {
    const request = platformAPI.hasFeature(featureName);
    pendingFeature[featureName] = request;

    // Mirror the result into the caches, then clear the pending slot whether the call succeeded
    // or failed — a rejected request must not wedge the slot (every later call would return the
    // same rejected promise) or surface as an unhandled rejection.
    request
      .then(r => {
        const item: FeatureCacheItem = { enabled: r.data.value, timeCached: Date.now() };
        memory.set(featureName, item);
        return cache.setItem<FeatureCacheItem>(featureName, item).catch(() => undefined);
      })
      .catch(() => undefined)
      .then(() => { pendingFeature[featureName] = null; });
  }

  return pendingFeature[featureName];
}

// Ask the API directly, falling back to `fallback` if the request fails. Callers use this when the
// caches cannot answer and waiting on the one-shot refresh is not an option.
const directCheck = (featureName: string, fallback = false): Promise<boolean> =>
  hasFeature(featureName).then(rsp => rsp.data.value).catch(() => fallback);

// A stalled IndexedDB transaction never resolves or rejects (see the note on `memory`), so a bare
// `await cache.getItem` can wedge the caller. Cap the wait and treat a timeout as a cache miss.
const readFromStorage = (featureName: string): Promise<FeatureCacheItem | null> => {
  let timer: ReturnType<typeof setTimeout>;
  const timedOut = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), storageReadTimeout); });

  return Promise.race([cache.getItem<FeatureCacheItem>(featureName).catch(() => null), timedOut])
    .then(value => { clearTimeout(timer); return value ?? null; });
};

/**
 * Wait for refreshFeatureCache to publish this key — but never indefinitely. If the refresh has
 * already settled it will not run again, so ask the API directly; otherwise subscribe, with a
 * timeout that resolves false so a caller such as SideNav still renders (without the gated item)
 * rather than never resolving at all.
 */
const awaitRefresh = (featureName: string): Promise<boolean> => {
  if (refreshSettled) return directCheck(featureName);

  return new Promise<boolean>(resolve => {
    let done = false;
    const settle = (enabled: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(enabled);
    };
    const timer = setTimeout(() => settle(false), subscriptionTimeout);
    pushSubscription(featureName, settle);
  });
};

export const getCachedFeature = async (featureName: string): Promise<boolean> => {
  const remembered = memory.get(featureName);
  if (remembered) {
    if (isExpired(remembered))
      return directCheck(featureName, remembered.enabled);

    return remembered.enabled;
  }

  // A storage failure must not resolve as "feature missing" — fall through to the subscription
  // path so refreshFeatureCache can answer instead.
  const result = await readFromStorage(featureName);

  if (result === undefined || result === null)
    return awaitRefresh(featureName);

  memory.set(featureName, result);

  if (isExpired(result))
    return directCheck(featureName, result.enabled);

  return result.enabled;
}
export const setCachedFeature = (featureName: string, enabled: boolean) => {
  const item: FeatureCacheItem = { enabled };
  if (expiredFeatures.includes(featureName))
    item.timeCached = Date.now();

  memory.set(featureName, item);

  return cache.setItem(featureName, item);
}

export const hasConsentFeature = async (consentName: string): Promise<boolean> => {
  const provider = Providers.globalProvider;
  if (ConsentFeatures[consentName].ConsentUrl && ConsentFeatures[consentName].ConsentUrl !== "") {
    const api = new platformService();
    try {
      const result = await api.checkConsent(ConsentFeatures[consentName].ConsentUrl);
      return result.data.value;
    }
    catch { }
  } else if (provider instanceof Msal2Provider) {
    try {
      await provider.testAccessToken(...ConsentFeatures[consentName].Scopes);
      return true;
    } catch { }
  }
  return false;
};
 

 